import { useRef, useState, type ChangeEvent, type DragEvent } from "react";

import { Button } from "@/components/ui/Button";
import { useUploadCoi } from "./api";

// Phase 2b coi-upload-and-review FR-1 — drag-drop + file-picker
// uploader. Accepts PDF + JPG/PNG/HEIC; rejects anything else before
// hitting the wire so customers see the message immediately.
// MAX_BYTES mirrors the server cap (FR-12 / AC-12).

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic";
const MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  onUploaded: (coiId: number) => void;
}

export function CoiUploader({ onUploaded }: Props): JSX.Element {
  const upload = useUploadCoi();
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function clientValidate(file: File): string | null {
    if (file.size > MAX_BYTES) return "File is larger than 25 MB.";
    if (!/(pdf|jpe?g|png|heic)$/i.test(file.name)) {
      return "Only PDF, JPG, PNG, or HEIC files are accepted.";
    }
    return null;
  }

  async function pick(file: File): Promise<void> {
    setLocalError(null);
    const reason = clientValidate(file);
    if (reason) {
      setLocalError(reason);
      return;
    }
    try {
      const doc = await upload.mutateAsync(file);
      onUploaded(doc.id);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Upload failed.");
    }
  }

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) void pick(file);
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void pick(file);
  }

  const dropClasses = dragging
    ? "border-brand-red bg-brand-red/5"
    : "border-zinc-300 bg-zinc-50";
  const dropClass = `flex flex-col items-center justify-center rounded-lg border-2 border-dashed ${dropClasses} px-4 py-12 transition`;

  return (
    <div className="space-y-4">
      <div
        className={dropClass}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <p className="text-sm text-zinc-700">Drag a PDF or image here, or</p>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          {upload.isPending ? "Uploading…" : "Choose file"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onChange}
          className="sr-only"
          aria-label="Select certificate of insurance"
        />
      </div>

      <p className="text-xs text-zinc-600">
        Required: General Liability ≥ $1M each occurrence, JDR named as
        additional insured and loss payee.
      </p>

      {localError && (
        <div role="alert" className="rounded border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
          {localError}
        </div>
      )}
    </div>
  );
}
